import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ScenarioCards.module.css';
import { ScenarioCharacter } from '../../api/types/scenario';
import { ScenarioAddButton, ScenarioDeleteButton } from './ScenarioCommonButtons';
import { useDevice } from '../../context/DeviceContext';
import CharacterDetailsModal from './CharacterDetailsModal';
import { charactersIcon } from '../../assets/icons';

interface ScenarioCharacterCardProps {
  character: ScenarioCharacter;
  showAddButton?: boolean;
  showDeleteButton?: boolean;
  onAddClick?: () => void;
  onDeleteClick?: () => void;
  isSearchResult?: boolean;
  onCharacterUpdated?: (updatedCharacter: ScenarioCharacter) => void;
}

export function ScenarioCharacterCard({ 
  character, 
  showAddButton = false, 
  showDeleteButton = false,
  onAddClick,
  onDeleteClick,
  isSearchResult = false,
  onCharacterUpdated
}: ScenarioCharacterCardProps): JSX.Element {
  const { isMobile } = useDevice();
  const navigate = useNavigate();
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [timestamp, setTimestamp] = useState<number>(Date.now());
  
  // Update timestamp when character changes
  useEffect(() => {
    setTimestamp(Date.now());
  }, [character]);
  
  const handleCardClick = async () => {
    if (isMobile && isSearchResult && onAddClick) {
      console.log('Mobile search card click - Add character:', character);
      onAddClick();
    } else {
      // Show character details modal for all characters, including search results
      console.log('Opening character details modal for:', character);
      setShowDetailsModal(true);
    }
  };

  // Add timestamp to image URL to prevent caching
  const getImageUrlWithTimestamp = (url: string | undefined) => {
    if (!url) return "https://placehold.co/100x100/252525/FFFFFF/png?text=?";
    return `${url}?t=${timestamp}`;
  };

  const handleCharacterUpdated = (updatedCharacter: ScenarioCharacter) => {
    // Update timestamp to force image refresh
    setTimestamp(Date.now());
    
    // Pass the update to the parent component
    if (onCharacterUpdated) {
      onCharacterUpdated(updatedCharacter);
    }
  };

  return (
    <>
      <div 
        className={`${styles.characterCard} ${isSearchResult ? styles.searchResultCard : ''}`}
        onClick={handleCardClick}
      >
        <img 
          src={getImageUrlWithTimestamp(character.icon)}
          alt={character.display_name} 
          className={styles.characterAvatar}
        />
        <div className={styles.characterInfo}>
          <h4 className={styles.characterName}>{character.display_name}</h4>
          <p className={styles.characterDescription}>
            {character.tagline}
          </p>
        </div>
        
        {/* X button in the top right corner */}
        {showDeleteButton && onDeleteClick && (
          <button 
            className={styles.deleteXButton}
            onClick={(e) => {
              e.stopPropagation();
              console.log('Delete character:', character);
              onDeleteClick();
            }}
            aria-label="Remove character"
          >
            ×
          </button>
        )}
        
        {/* Desktop add button */}
        {!isMobile && showAddButton && onAddClick && (
          <div 
            className={styles.cardActionButton}
            onClick={(e) => e.stopPropagation()}
          >
            <ScenarioAddButton 
              onClick={() => {
                console.log('Add character:', character);
                onAddClick();
              }}
              showPill={false}
            />
          </div>
        )}
      </div>

      {/* Character Details Modal */}
      <CharacterDetailsModal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        character={character}
        onCharacterUpdated={handleCharacterUpdated}
      />
    </>
  );
}

interface AdventureCharacterCardProps {
  character?: ScenarioCharacter;
  showSwapIcon?: boolean;
  isLoading?: boolean;
}

export function AdventureCharacterCard({ character, showSwapIcon = false, isLoading = false }: AdventureCharacterCardProps): JSX.Element {
  return (
    <div className={styles.personaModalWrapper}>
      <div className={`${styles.personaModal} ${showSwapIcon ? styles.withSwapIcon : ''} ${styles.adventureCharacter}`}>
        <div className={styles.personaContent}>
          {isLoading ? (
            <div className={styles.loadingIndicator}>Loading character...</div>
          ) : (
            <>
              <img 
                src={character?.icon || charactersIcon} 
                alt={character?.display_name || "Select a character"} 
                className={styles.personaAvatar}
              />
              <div className={styles.personaInfo}>
                <h4 className={styles.personaName}>
                  {character?.display_name || "Select a character"}
                </h4>
              </div>
            </>
          )}
        </div>
        {showSwapIcon && (
          <div className={styles.swapIconWrapper}>
            <span className={styles.swapIcon}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 16L3 12M3 12L7 8M3 12H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M17 8L21 12M21 12L17 16M21 12H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdventureCharacterCard; 